import { STORAGE_KEYS } from '@constants/storage';

import { ComponentType } from '../types';
import { getOrgSegment, getUserId, ScopedStorage } from './storageSegments';

/**
 * Outer scope = userId, inner scope = orgId → string[].
 * Each user gets their own nested map so logout preserves only one key.
 */
type OrgViews = Record<string, string[]>;

const store = new ScopedStorage<OrgViews>(
  STORAGE_KEYS.DYNAMIC_COMPONENT_MIXPANEL_VIEWS,
  {}
);

const getOrgKeys = (): Set<string> => {
  const views = store.read(getUserId());
  const arr = views[getOrgSegment()];
  if (!Array.isArray(arr)) return new Set();
  return new Set(
    arr.filter((k): k is string => typeof k === 'string' && k.trim().length > 0)
  );
};

/** Stable id for deduping Mixpanel `DYNAMIC_COMPONENT_VIEWED` per user. */
export const buildDynamicComponentViewKey = (
  componentType: ComponentType,
  id: string
): string => `${componentType}:${String(id).trim()}`;

/**
 * Returns true only the first time this user+org has recorded a view for
 * `instanceKey`, then persists. Each dynamic component instance (banner/popup
 * id) therefore contributes at most one "viewed" event per user per org,
 * including across carousel rotation and re-renders.
 */
export function tryMarkMixpanelViewSent(instanceKey: string): boolean {
  const normalizedKey = instanceKey.trim();
  if (!normalizedKey) return false;

  const keys = getOrgKeys();
  if (keys.has(normalizedKey)) return false;

  keys.add(normalizedKey);

  const user = getUserId();
  const org = getOrgSegment();
  const current = store.read(user);
  store.write(user, { ...current, [org]: [...keys] });
  return true;
}
